import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, ConfirmDialog, DataGrid, StatusBadge, useToast, type DataGridColumn } from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Role } from '@kss/api';
import { roleService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

export default function AllRoles() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const perms = useModulePerms('Role');
  const [rows, setRows] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await roleService.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.roleId) return;
    setDeleting(true);
    try { await roleService.delete(confirmDelete.roleId); toast.success('Deleted', `${confirmDelete.name} removed`); setConfirmDelete(null); await load(); }
    catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<Role>[] = [
    // The live Role row DOES carry a `code` column (verified
    // 2026-05-05) — currently empty for all seed rows but
    // addressable. `isSystem` distinguishes built-in roles
    // (SuperAdmin / Manager / Customer) from custom ones.
    { accessor: 'code', title: 'Code', sortable: true, filterable: true, width: 120, render: (r) => r.code || '—' },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'description', title: 'Description', sortable: true, render: (r) => r.description || '—' },
    {
      accessor: 'isSystem',
      title: 'System',
      sortable: true,
      width: 110,
      render: (r) =>
        (r as Role & { isSystem?: boolean }).isSystem ? (
          <StatusBadge status="active" />
        ) : (
          <span className="text-xs text-content-muted">custom</span>
        ),
    },
    { accessor: 'isActive', title: 'Status', sortable: true, render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} /> },
    { accessor: 'roleId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); if (r.roleId) navigate(`/panel/roles/${encodeId(r.roleId)}`); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Role>
        title={<span className="hidden sm:inline">All roles</span>}
        columnPicker data={rows} columns={columns} defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <StandardAddDropdown basePath="/panel/roles" newLabel="New role" service={roleService} templateFields={['name', 'code', 'description', 'isSystem']} module="Role" />
          </div>
        }
      />
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))} onConfirm={handleDelete}
        title="Delete role" message={confirmDelete ? `Remove "${confirmDelete.name}"?` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}
